// Hardcoded demo mode: this used to be an axios client talking to the Flask
// backend. There is no backend anymore - every export below is a local,
// synchronous (but still Promise-wrapped, so callers don't need to change)
// stand-in backed by localHistory.js. Nothing here makes a network call.
import {
  addSchedule,
  deleteScheduleRow,
  getAllDetections,
  getAvailableDates,
  getDetections,
  getEmailSettings,
  getIntegrations,
  getIntegrationRow,
  getScheduleRow,
  getSchedules,
  getSummary,
  getSessionSummary,
  resolveDetectionRow,
  saveEmailSettingsRow,
  saveIntegrationRow,
  clearAllDetections,
  clearDate,
  todayKey,
  touchEmailSent,
  touchIntegrationTested,
  touchScheduleSent,
  updateScheduleRow,
  deleteIntegrationRow,
} from './localHistory';

const resolveDateKey = (date) => (date === 'today' ? todayKey() : date);

export const getApiBaseUrl = () => 'demo-mode (no backend)';

// The two real exceptions to "no backend": actually delivering an email has
// to happen server-side (SMTP credentials can never live in the browser
// bundle) - see sendScheduleNow() below - and the sample video clips are too
// heavy (~110MB total) to ship inside the frontend bundle, so they're
// deployed straight onto the server into backend/videos/ and streamed back
// from there instead - see videoUrl() below. Both are served by the same
// small standalone Flask service, backend/email_server.py. Everything else
// in this file stays a local/simulated stand-in.
const EMAIL_API_URL = process.env.REACT_APP_EMAIL_API_URL || '/backend';
const VIDEO_API_URL = process.env.REACT_APP_VIDEO_API_URL || EMAIL_API_URL;

// Builds the URL for a sample clip served by backend/email_server.py's
// /videos/<filename> route (byte-range aware, so seeking/scrubbing is
// smooth instead of re-downloading the whole clip). `file` is the bare
// filename, e.g. "bread.mp4" - see frontend/src/scenarios.js.
export const videoUrl = (file) => `${VIDEO_API_URL}/videos/${file}`;

const ok = (data) => Promise.resolve({ data });
const fail = (message) => Promise.reject({ response: { data: { error: message } } });

export const fetchHealth = () =>
  ok({ status: 'running (hardcoded demo mode)', model_loaded: false, timestamp: new Date().toISOString() });

export const fetchAvailableDates = () =>
  ok({
    dates: getAvailableDates().map((key) => ({
      key,
      label: key === todayKey() ? `Today (${key})` : key,
    })),
  });

export const fetchDetections = (date = 'today', camera = 'all', limit = 100) =>
  ok({ detections: getDetections({ date: resolveDateKey(date), camera, limit }), date: resolveDateKey(date) });

export const fetchAllDetections = (limit = 10000) =>
  ok({ detections: getAllDetections().slice(0, limit) });

export const fetchSummary = (date = 'today') => ok(getSummary(resolveDateKey(date)));

export const fetchSessionSummary = () => ok(getSessionSummary());

export const resolveDetection = (id) => {
  resolveDetectionRow(id);
  return ok({ status: 'resolved', id });
};

// No real camera/model to start or stop - kept only so nothing throws if
// something still calls these.
export const startStream = () => ok({ status: 'not applicable in demo mode' });
export const stopStream = () => ok({ status: 'not applicable in demo mode' });
export const stopAllStreams = () => ok({ status: 'not applicable in demo mode' });

export const resetTodaySession = (date) => {
  const target = date || todayKey();
  clearDate(target);
  return ok({ status: 'reset', date: target, removed_evidence_files: 0 });
};

// Used by the Dashboard's "Clear session log" button - unlike
// resetTodaySession above (scoped to one date), this wipes every detection
// the session has logged so far, matching the fact that the Dashboard's log
// is now a running session view, not filtered to whichever date is selected.
export const resetSession = () => {
  clearAllDetections();
  return ok({ status: 'reset' });
};

export const updateConfidence = (confidence) => ok({ status: 'updated', confidence });

export const fetchEvidence = (detectionId) => {
  const row = getAllDetections().find((r) => r.id === detectionId);
  if (!row || !row.evidence) return fail('Not found');
  return ok({
    image: row.evidence,
    path: null,
    product_id: row.product_id,
    batch_id: row.batch_id,
    product_name: row.product_name,
    line_id: row.line_id,
    shift: row.shift,
    mfg_date: row.mfg_date,
    expiry_date: row.expiry_date,
    unit_number: row.unit_number,
    qr_payload: row.qr_payload,
  });
};

// ---- Email settings / schedules (mocked send - demo mode never emails anyone) --

export const fetchEmailSettings = () => ok(getEmailSettings());

export const saveEmailSettings = (payload) => ok({ status: 'saved', settings: saveEmailSettingsRow(payload) });

export const sendEmailNow = (payload) => {
  const settings = getEmailSettings();
  const recipient = (payload?.recipient || settings.recipient || '').trim();
  if (!recipient) return fail('Recipient email is required');
  touchEmailSent();
  return ok({
    status: 'sent',
    message: 'Demo mode - email simulated locally, no message was actually sent.',
    recipient,
    date: payload?.date || todayKey(),
  });
};

export const fetchSchedules = () => ok({ schedules: getSchedules() });

export const createSchedule = (payload) => {
  const recipient = (payload.recipient || '').trim();
  if (!recipient || !recipient.includes('@')) return fail('Invalid recipient');
  const schedule = addSchedule({ ...payload, recipient });
  return ok({ status: 'added', id: schedule.id, schedule });
};

export const updateSchedule = (id, payload) => {
  const updated = updateScheduleRow(id, payload);
  if (!updated) return fail('Schedule not found');
  return ok({ status: 'updated', schedule: updated });
};

export const deleteSchedule = (id) => {
  deleteScheduleRow(id);
  return ok({ status: 'deleted', id });
};

// Only a "manual" schedule's Send button triggers a real email - daily/
// weekly/hourly/instant-on-detection stay simulated (kept selectable in the
// UI, per product decision, but never actually fire on their own; there's
// no scheduler running client-side to trigger them anyway). `context` lets
// the caller pass the currently-loaded detections/date straight from
// Dashboard state instead of this function guessing which day to report on.
export const sendScheduleNow = async (id, context = {}) => {
  const sched = getScheduleRow(id);
  if (!sched) return fail('Schedule not found');
  if (!sched.recipient) return fail('No recipient on this schedule');

  if (sched.frequency !== 'manual') {
    touchScheduleSent(id);
    return ok({
      status: 'sent',
      simulated: true,
      recipient: sched.recipient,
      message: `Demo mode - "${sched.frequency}" sends are simulated here; only a manual Send actually emails.`,
    });
  }

  const date = context.date || todayKey();
  const camera = context.camera || 'All cameras';
  const detections = context.detections || getDetections({ date, limit: 500 });

  let response;
  try {
    response = await fetch(`${EMAIL_API_URL}/api/send-report-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipient: sched.recipient, date, camera, detections }),
    });
  } catch {
    return fail(`Could not reach the email server at ${EMAIL_API_URL}. Start it with: python backend/email_server.py`);
  }

  const body = await response.json().catch(() => ({}));
  if (!response.ok) return fail(body.error || `Email server responded with ${response.status}`);

  touchScheduleSent(id);
  return ok({ status: 'sent', simulated: false, recipient: sched.recipient, message: body.message });
};

// ---- Export / Integrations (CSV+JSON are real, generated from local data;
// webhook/CRM pushes are mocked - no outbound network call in demo mode) ----

const EXPORT_FIELDS = [
  'id', 'camera', 'date', 'timestamp', 'class_name',
  'product_id', 'batch_id', 'product_name', 'line_id', 'shift',
  'mfg_date', 'expiry_date', 'unit_number', 'qr_payload',
  'confidence', 'max_confidence', 'first_seen', 'last_seen',
  'first_seen_at', 'last_seen_at', 'duration_sec', 'occurrences',
  'resolved', 'resolved_at',
];

function csvEscape(value) {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function rowsToCsv(rows) {
  const header = EXPORT_FIELDS.join(',');
  const lines = rows.map((row) => EXPORT_FIELDS.map((field) => csvEscape(row[field])).join(','));
  return [header, ...lines].join('\n');
}

function blobUrl(content, type) {
  const blob = new Blob([content], { type });
  return URL.createObjectURL(blob);
}

function getExportRows(date, camera) {
  const resolvedDate = date === 'today' || !date ? todayKey() : date;
  return getDetections({ date: resolvedDate, camera: camera || 'all', limit: 5000 });
}

export const getExportCsvUrl = (date = 'today', camera = 'all') => blobUrl(rowsToCsv(getExportRows(date, camera)), 'text/csv');

export const getExportJsonUrl = (date = 'today', camera = 'all') => {
  const rows = getExportRows(date, camera);
  const payload = {
    integration_type: 'json_api',
    exported_at: new Date().toISOString(),
    filters: { date: date === 'today' ? todayKey() : date, camera },
    count: rows.length,
    records: rows,
  };
  return blobUrl(JSON.stringify(payload, null, 2), 'application/json');
};

export const getDownloadDateUrl = (date) => {
  const rows = getExportRows(date, 'all');
  return blobUrl(JSON.stringify(rows, null, 2), 'application/json');
};

export const getDownloadAllUrl = () => blobUrl(JSON.stringify(getAllDetections(), null, 2), 'application/json');

export const fetchExportSchema = () =>
  ok({
    fields: EXPORT_FIELDS,
    json_feed_url: 'demo mode - no live feed URL (data is generated in-browser)',
    csv_feed_url: 'demo mode - no live feed URL (data is generated in-browser)',
  });

export const fetchIntegrations = () => ok({ integrations: getIntegrations() });

export const createIntegration = (payload) => {
  if (!(payload.name || '').trim()) return fail('Name is required');
  if (!['webhook', 'crm'].includes((payload.type || '').toLowerCase())) return fail('Unsupported integration type');
  return ok({ status: 'saved', integration: saveIntegrationRow(payload) });
};

export const updateIntegration = (id, payload) => {
  const existing = getIntegrationRow(id);
  if (!existing) return fail('Integration not found');
  return ok({ status: 'updated', integration: saveIntegrationRow({ ...existing, ...payload, id }) });
};

export const deleteIntegration = (id) => {
  deleteIntegrationRow(id);
  return ok({ status: 'deleted', id });
};

export const testIntegration = (payload) => {
  const rows = getExportRows(payload.date, payload.camera).slice(0, 25);
  const preview = {
    integration_type: payload.type,
    exported_at: new Date().toISOString(),
    filters: { date: payload.date, camera: payload.camera },
    count: rows.length,
    records: rows,
  };
  if (payload.integration_id) touchIntegrationTested(payload.integration_id);
  return ok({
    status: 'validated',
    message: `Demo mode - this is a local preview only. In a live deployment this would push ${rows.length} record(s) to your ${payload.type === 'crm' ? 'CRM' : 'webhook'} endpoint.`,
    preview_count: rows.length,
    preview,
  });
};

export const exportIntegrationNow = (id, payload) => {
  const integration = getIntegrationRow(id);
  if (!integration) return fail('Integration not found');
  const rows = getExportRows(payload?.date, payload?.camera);
  touchIntegrationTested(id);
  return ok({
    status: 'exported',
    message: `Demo mode - simulated export of ${rows.length} record(s). No outbound network call was made.`,
    export_count: rows.length,
  });
};

export default { getApiBaseUrl };
